<script setup lang="ts">
import SvgLoader from '@/utils/SvgLoader.vue';
import Modal from '@/components/modal/Modal.vue';
import Button from '@/components/input/Btn/Btn.vue';
import { LottieAnimation } from 'lottie-web-vue';
import loadingAnimation from '@/assets/loadingAnimation.json';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';

/**
 * Iconの種類
 */
type IconType = '' | 'info' | 'error' | 'loading';
/**
 * colorStyleの種類
 */
type ColorStyle = 'primary' | 'error';

const props = withDefaults(
  defineProps<{
    modalState: boolean;
    backButtonLabel?: string;
    nextButtonLabel?: string;
    icon?: IconType;
    colorStyle?: ColorStyle;
    enableOutsideClick?: boolean;
    modalWidth?: number;
    isDisabledNextButton?: boolean;
    isDisabledScroll?: boolean;
    noDescription?: boolean;
    testId?: string;
    progressValue?: number;
    progressMax?: number;
    isCenterContent?: boolean;
    useKeyboard?: boolean;
  }>(),
  {
    icon: '',
    backButtonLabel: '',
    nextButtonLabel: '',
    colorStyle: 'primary',
    enableOutsideClick: true,
    modalWidth: undefined,
    isDisabledNextButton: undefined,
    isDisabledScroll: undefined,
    noDescription: false,
    testId: undefined,
    progressValue: undefined,
    progressMax: 100,
    isCenterContent: false,
    useKeyboard: false,
  }
);

const emits = defineEmits(['onBack', 'onNext']);

const iconPath = computed(() => {
  switch (props.icon) {
    case 'info':
      return 'modalInfoIcon';
    case 'error':
      return 'modalErrorInfoIcon';
    default:
      return '';
  }
});

const suffixMainColorCSS = computed(() => {
  switch (props.colorStyle) {
    case 'primary':
      return '--primary';
    case 'error':
      return '--error';
    default:
      return '--primary';
  }
});

const modalSize = computed(() => {
  return props.modalWidth ?? 'max-content';
});

const hasProgress = computed(() => props.progressValue !== undefined);

const progressPercentText = computed(() => {
  if (props.progressValue === undefined) return '';
  const max = props.progressMax ?? 100;
  const safeValue = Math.min(Math.max(props.progressValue, 0), max);
  return `${Math.round((safeValue / max) * 100)}%`;
});

/**
 * 戻るボタンクリック時の処理
 */
const handleBack = () => {
  if (props.modalState) {
    emits('onBack');
  }
};

/**
 * 次へボタンクリック時の処理
 */
const handleNext = () => {
  emits('onNext');
};

const nextButtonRef = ref<{ focus: () => void } | null>(null);

/**
 * ESCキー押下のイベントハンドラ
 * @param e - キーボードイベント
 */
const keydownEvent = (e: KeyboardEvent) => {
  if (e.key === 'Escape' && props.useKeyboard) {
    e.preventDefault();
    handleBack();
  }
};

/**
 * モーダルオープン時の処理
 */
const openModal = async () => {
  window.addEventListener('keydown', keydownEvent);
  if (props.useKeyboard) {
    await nextTick();
    nextButtonRef.value?.focus();
  }
};

/**
 * モーダルクローズ時の処理
 */
const closeModal = () => {
  window.removeEventListener('keydown', keydownEvent);
};

watch(
  () => props.modalState,
  async (nv) => {
    if (nv) {
      await openModal();
    } else {
      closeModal();
    }
  }
);

onMounted(async () => {
  if (props.modalState) await openModal();
});

onBeforeUnmount(() => {
  if (props.modalState) closeModal();
});
</script>
<template>
  <Modal
    :modal-state="modalState"
    padding="0"
    border-radius="16px"
    :no-padding-and-margin="false"
    :modal-size="modalSize"
    :is-force-no-style="true"
    :md-click-out-side-to-close="enableOutsideClick"
    :scroll-off="isDisabledScroll"
    :no-cancel="true"
    @outside-click="handleBack"
  >
    <div class="modal" :data-testid="testId || undefined">
      <div v-if="icon !== ''" class="modal__icon">
        <LottieAnimation
          v-if="icon === 'loading'"
          ref="anim"
          :animation-data="loadingAnimation"
          :auto-play="true"
          :loop="true"
          :speed="1"
        />
        <SvgLoader v-else :class="`modal__icon--${iconPath}`" :name="iconPath" />
      </div>
      <h1 class="modal__title"><slot name="title" /></h1>
      <p v-if="!noDescription" class="modal__description"><slot name="description" /></p>
      <div v-if="hasProgress" class="modal__progress">
        <div v-if="$slots.progressLabel" class="modal__progress-label">
          <slot name="progressLabel" />
        </div>
        <progress class="modal__progress-bar" :max="progressMax" :value="progressValue" />
        <div class="modal__progress-meta">
          <div class="modal__progress-message">
            <slot name="progressMessage" />
          </div>
          <span class="modal__progress-value">{{ progressPercentText }}</span>
        </div>
      </div>
      <div v-if="$slots.default" class="modal__body" :class="{ 'modal__body--center': isCenterContent }">
        <slot />
      </div>
      <div v-if="backButtonLabel !== '' || nextButtonLabel !== ''" class="modal__footer">
        <div class="modal__button-container">
          <Button
            v-if="backButtonLabel !== ''"
            :class="`modal__button modal__button--cancel modal__button--cancel${suffixMainColorCSS}`"
            @click="handleBack"
          >
            {{ backButtonLabel }}
          </Button>
          <slot name="betweenButtons" />
          <Button
            v-if="nextButtonLabel !== ''"
            ref="nextButtonRef"
            :class="`modal__button modal__button--save modal__button--save${suffixMainColorCSS}`"
            :disabled="isDisabledNextButton"
            @click="handleNext"
          >
            {{ nextButtonLabel }}
          </Button>
        </div>
        <slot name="footer" />
      </div>
    </div>
  </Modal>
</template>
<style lang="scss" scoped>
.modal {
  display: grid;
  gap: 24px;
  padding: 24px 16px;
  background-color: $semanticWhite;
  border-radius: 16px;
  min-width: 500px;

  &__icon {
    display: grid;
    justify-content: center;

    &--modalErrorInfoIcon {
      opacity: 30%;
    }
  }

  &__title {
    font-family: Noto Sans JP;
    font-size: 22px;
    font-weight: 700;
    line-height: 28.6px;
    text-align: center;

    color: $textDefault;
  }

  &__description {
    font-family: Noto Sans JP;
    font-size: 14px;
    font-weight: 400;
    line-height: 18.2px;
    text-align: center;

    color: $textDefault;
  }

  &__progress {
    display: grid;
    gap: 8px;
    padding: 4px 8px 0;
  }

  &__progress-label {
    font-size: 14px;
    font-weight: 600;
    color: $textDefault;
    text-align: left;
  }

  &__progress-bar {
    width: min(640px, 100%);
    height: 12px;
    border-radius: 999px;
    overflow: hidden;
    background-color: $surfaceSecondary;
    appearance: none;

    &::-webkit-progress-bar {
      background-color: $surfaceSecondary;
      border-radius: 999px;
    }

    &::-webkit-progress-value {
      background-image: linear-gradient(90deg, $semanticPrimary 0%, $semanticPrimary 100%);
      border-radius: 999px;
    }

    &::-moz-progress-bar {
      background-image: linear-gradient(90deg, $semanticPrimary 0%, $semanticPrimary 100%);
      border-radius: 999px;
    }
  }

  &__progress-meta {
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: center;
    font-size: 12px;
    line-height: 16px;
    column-gap: 12px;
  }

  &__progress-message {
    color: $textDefault;
    font-weight: 600;
  }

  &__progress-value {
    color: $semanticPrimary;
    font-weight: 700;
  }

  &__body {
    display: grid;
    gap: 12px;

    &--center {
      text-align: center;
      justify-items: center;
    }
  }

  &__footer {
    display: grid;
    gap: 16px;
    justify-items: center;
  }

  &__button {
    min-width: 160px;
    height: 40px;
    font-family: Noto Sans JP;
    font-size: 16px;
    font-weight: 700;
    line-height: 40px;
    letter-spacing: 0em;
    text-align: center;
    border-radius: 4px;
    padding: 0 8px;
    box-shadow: none;

    &--cancel {
      background-color: $semanticWhite;

      &--primary {
        color: $semanticPrimary;
        border: 1px solid $semanticPrimary;
      }

      &--error {
        color: $semanticError;
        border: 1px solid $semanticError;
      }
    }
    &--save {
      color: $semanticWhite;

      &--primary {
        background-color: $semanticPrimary;
        border: 1px solid $semanticPrimary;
      }

      &--error {
        background-color: $semanticError;
        border: 1px solid $semanticError;
      }

      &:disabled {
        background-color: $surfaceSecondary;
        border: 1px solid $surfaceSecondary;
        color: $textDisable;
      }
    }

    &-container {
      display: grid;
      gap: 10px;
      grid-auto-flow: column;
      justify-content: center;
      padding-top: 16px;
    }
  }
}
</style>
